// Preset Export/Import — JSON file format
// Exports presets (without snapshots) as a portable JSON file
// Import merges into existing presets, skipping duplicates by name

import * as presetStore from './preset-store';
import type { Preset } from './preset-types';

// ==================== Export Format ====================

interface PresetExportFile {
  version: 1;
  exportedAt: string;
  appName: 'VeriGit';
  presets: Preset[];
}

// ==================== Export ====================

export async function exportPresets(presetIds?: string[]): Promise<void> {
  let presets = await presetStore.getAllPresets();

  // If specific IDs provided, filter
  if (presetIds && presetIds.length > 0) {
    const idSet = new Set(presetIds);
    presets = presets.filter(p => idSet.has(p.id));
  }

  if (presets.length === 0) {
    throw new Error('No presets to export');
  }

  const exportData: PresetExportFile = {
    version: 1,
    exportedAt: new Date().toISOString(),
    appName: 'VeriGit',
    presets,
  };

  const json = JSON.stringify(exportData, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `verigit-presets-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ==================== Import ====================

export interface ImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export async function importPresets(file: File): Promise<ImportResult> {
  const text = await file.text();
  let parsed: PresetExportFile;

  try {
    parsed = JSON.parse(text);
  } catch {
    return { imported: 0, skipped: 0, errors: ['Invalid JSON file'] };
  }

  if (parsed.appName !== 'VeriGit' || parsed.version !== 1) {
    return { imported: 0, skipped: 0, errors: ['Not a valid VeriGit preset export file'] };
  }

  if (!Array.isArray(parsed.presets) || parsed.presets.length === 0) {
    return { imported: 0, skipped: 0, errors: ['No presets found in file'] };
  }

  const existing = await presetStore.getAllPresets();
  const existingNames = new Set(existing.map(p => p.name.toLowerCase()));

  let imported = 0;
  let skipped = 0;
  const errors: string[] = [];

  for (const preset of parsed.presets) {
    try {
      // Skip duplicates by name
      if (existingNames.has(preset.name.toLowerCase())) {
        skipped++;
        continue;
      }

      // Assign new ID and sort order to avoid conflicts
      const newPreset: Preset = {
        ...preset,
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sortOrder: await presetStore.getNextSortOrder(),
        pinned: false, // Don't pin imported presets
      };

      await presetStore.savePreset(newPreset);
      existingNames.add(newPreset.name.toLowerCase());
      imported++;
    } catch (err: any) {
      errors.push(`Failed to import "${preset.name}": ${err.message}`);
    }
  }

  return { imported, skipped, errors };
}

// ==================== File picker helper ====================

export function pickPresetFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = () => {
      const file = input.files?.[0] || null;
      resolve(file);
    };
    // Handle cancel
    input.oncancel = () => resolve(null);
    input.click();
  });
}
